import {
  faDownload,
  faMagnifyingGlass,
  faPlus,
  faSliders,
  faTableColumns,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useRouteMatch } from 'react-router-dom';

import { Breadcrumbs, Button, Input, PageHeader, Table, toast, Toolbar } from '@feathr/components';

import campaignColumns from '../columns/campaignColumns';
import CampaignTypeModal from '../components/CampaignTypeModal';
import type { ISampleCampaign, TSampleCampaignChannel } from '../sampleData';
import { sampleCampaigns } from '../sampleData';
import { buildPrototypeBreadcrumbs } from '../utils/buildBreadcrumbs';

import * as shellStyles from '../Shell/PrototypeShell.css';

type TChannelRoute = TSampleCampaignChannel | 'all' | 'other';

function getTitle(t: TFunction, channel: TChannelRoute): string {
  switch (channel) {
    case 'all':
      return t('All Marketing');

    case 'ads':
      return t('Ads');

    case 'email':
      return t('Email Marketing');

    case 'monetization':
      return t('Monetization Campaigns');

    case 'search':
      return t('Search Marketing');

    case 'other':
      return t('Other Marketing');
  }
}

function MarketingPage(): JSX.Element {
  const { t } = useTranslation();
  const { channel: channelParam } = useParams<{ channel: string }>();
  const channel = (channelParam ?? 'all') as TChannelRoute;
  const match = useRouteMatch<{ accountId: string }>('/:accountId/ia-prototype');
  const base = match?.url ?? '';
  const [search, setSearch] = useState<string>('');
  const [isTypeModalOpen, setTypeModalOpen] = useState<boolean>(false);

  const breadcrumbItems = buildPrototypeBreadcrumbs(
    base,
    { label: t('Marketing'), defaultPath: '/marketing/all' },
    getTitle(t, channel),
    `/marketing/${channel}`,
  );

  const filtered = useMemo<ISampleCampaign[]>(() => {
    let result = sampleCampaigns;
    if (channel !== 'all' && channel !== 'other') {
      result = result.filter((c) => c.channel === channel);
    }
    if (channel === 'other') {
      result = result.filter((c) => c.channel === 'search');
    }
    if (search.trim()) {
      const lower = search.trim().toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(lower));
    }
    return result;
  }, [channel, search]);

  const columns = useMemo(() => campaignColumns({ base, t }), [base, t]);

  const filterElements = [
    <Box key={'search'} w={320}>
      <Input
        isClearable={true}
        onChange={(next?: string): void => setSearch(next ?? '')}
        placeholder={t('Search by name…')}
        prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
        type={'text'}
        value={search}
      />
    </Box>,
    <Toolbar align={'right'} key={'filters'}>
      <Button prefix={<FontAwesomeIcon icon={faSliders} />} type={'secondary'}>
        {t('Filters')}
      </Button>
      <Button prefix={<FontAwesomeIcon icon={faTableColumns} />} type={'secondary'}>
        {t('Customize columns')}
      </Button>
    </Toolbar>,
  ];

  const actions = (
    <Toolbar>
      <Button prefix={<FontAwesomeIcon icon={faDownload} />} type={'secondary'}>
        {t('Export as CSV')}
      </Button>
      <Button
        onClick={(): void => setTypeModalOpen(true)}
        prefix={<FontAwesomeIcon icon={faPlus} />}
        type={'primary'}
      >
        {t('Create')}
      </Button>
    </Toolbar>
  );

  return (
    <>
      <PageHeader
        actions={actions}
        BreadcrumbsComponent={<Breadcrumbs breadcrumbItems={breadcrumbItems} />}
        title={getTitle(t, channel)}
      />
      <div className={shellStyles.body}>
        <Table<ISampleCampaign>
          columns={columns}
          filterElements={filterElements}
          filterLabel={<></>}
          idKey={'id'}
          isPaginated={false}
          items={filtered}
        />
      </div>

      <CampaignTypeModal
        onClose={(): void => setTypeModalOpen(false)}
        onSelect={(typeName): void => {
          setTypeModalOpen(false);
          toast(
            t('Selected "{{type}}". The next step opens here in production.', { type: typeName }),
            {
              type: 'success',
            },
          );
        }}
        opened={isTypeModalOpen}
      />
    </>
  );
}

export default MarketingPage;
